import React from "react";
import { formatCurrency } from "../../../utils/formatters";
import {
  getItemQuantity,
  getLineAmount,
  getLineDisplayName,
  toNumberOrNull,
} from "./qboLine";
import { describeMaterialError, lineErrorText } from "./lineErrors";
import { isAiDrafted, isAiDraftPending } from "./composerModel";
import { childRollupSummary } from "./childModel";
import {
  MATERIAL_FAILURE_RAIL_NOTE,
  STANDALONE_QUOTED_LINE_BADGE,
  CHILD_ZERO_LABOR_RAIL_WARNING,
  NEGATIVE_LABOR_OVERRIDE_WARNING,
  ZERO_LABOR_RAIL_WARNING,
  hasNegativeLaborOverride,
  zeroLaborNeedsConfirmation,
} from "./traceLabels";
import { pricedAsStandaloneQuotedLine } from "./quotedLine";

/**
 * The left rail: everything in this job, one row per line item.
 *
 * A row shows what it is, how many, what it is made of, and what it comes to.
 * Anything the backend flagged — no rate card, an ambiguous material class, a
 * missing finish rate, a hard error — raises a dot so the estimator can see
 * from the rail alone which line needs attention.
 */

const JOB_EXTRAS_LABEL = "Delivery, install & extras";

function dimensionSummary(lineItem) {
  const parts = ["length", "width", "height"]
    .map((key) => toNumberOrNull(lineItem?.[key]))
    .filter((value) => value !== null && value > 0)
    .map((value) => String(Number(value)));
  return parts.length ? `${parts.join(" × ")}″` : null;
}

function lineWarnings(result, lineItem = null, isQuotedLine = false) {
  const childWarning = (lineItem?.children || []).find(
    (child) =>
      zeroLaborNeedsConfirmation(child) || hasNegativeLaborOverride(child),
  );
  const childLaborWarning = childWarning
    ? hasNegativeLaborOverride(childWarning)
      ? NEGATIVE_LABOR_OVERRIDE_WARNING
      : CHILD_ZERO_LABOR_RAIL_WARNING
    : null;
  return [
    // A line whose shop labor was zeroed by hand is a fact about the inputs,
    // not about the run — it raises a dot before the job re-prices.
    zeroLaborNeedsConfirmation(lineItem) ? ZERO_LABOR_RAIL_WARNING : null,
    hasNegativeLaborOverride(lineItem) ? NEGATIVE_LABOR_OVERRIDE_WARNING : null,
    childLaborWarning,
    // Never a raw validation dict in a tooltip.
    result?.error ? lineErrorText(result.error) : null,
    result?.material_error
      ? describeMaterialError(result.material_error).message
      : result?.material_ambiguous
        ? "More than one material fits this class at different prices."
        : null,
    // A quoted line has no product type on purpose, so "no rate card matched"
    // is the expected outcome, not a defect worth a dot.
    result?.rate_card_resolved?.labor_card_missing && !isQuotedLine
      ? "No rate card matches this line — no shop labor was charged."
      : null,
    result?.finish_rate_missing
      ? "A finish was chosen but the rate card has no finish rate."
      : null,
  ].filter(Boolean);
}

/** Rail amount: the backend's own line total, else unit price × pieces. */
function railAmount(result) {
  if (!result) return null;
  const total = toNumberOrNull(result.calculated_total_cost);
  if (total !== null) return { value: total, kind: "cost" };
  const amount = getLineAmount(result);
  return amount ? { value: amount, kind: "price" } : null;
}

function JobContentsRail({
  lineItems = [],
  results = null,
  availableProductTypes = [],
  selectedIndex = 0,
  onSelect,
  onAddLine,
  extrasIndex = null,
  extrasSummary = null,
  onDraftWithAi = null,
  composerAvailable = false,
}) {
  const typeName = (key) =>
    (availableProductTypes || []).find((type) => type.key === key)
      ?.display_name || null;

  return (
    <div className="est-rail">
      {lineItems.map((lineItem, index) => {
        const result = results?.line_item_results?.[index] || null;
        const isCustom = lineItem.mode === "custom";
        const typedType = !isCustom
          ? typeName(lineItem.product_type_key)
          : null;
        const typedName = String(lineItem.name || "").trim() || null;
        // A line priced from a quoted price alone is finished work, not a line
        // still waiting for a product type — so it never reads "Pick a product
        // type", and its row says what it is instead.
        const quotedLine =
          !isCustom && pricedAsStandaloneQuotedLine(result, lineItem);
        // An unnamed custom line is named by what it is made of, so two
        // different blank lines do not collapse into the same generic label. A
        // typed name always wins on a Product-mode line too — it is what an
        // estimator wrote down to match this line back to a drawing.
        const name = isCustom
          ? typedName ||
            getLineDisplayName(result, lineItem) ||
            "Custom / one-off"
          : typedName ||
            typedType ||
            result?.product_name ||
            (quotedLine ? STANDALONE_QUOTED_LINE_BADGE : "Pick a product type");
        // When a typed name is present AND differs from the type name, the
        // type joins the detail line instead of vanishing entirely.
        const typeInDetail =
          !isCustom && typedName && typedType && typedName !== typedType
            ? typedType
            : null;
        const quantity = getItemQuantity({ quantity: lineItem.quantity });
        const dimensions = dimensionSummary(lineItem);
        const material = lineItem.material_classification || null;
        const warnings = lineWarnings(result, lineItem, quotedLine);
        const amount = railAmount(result);
        const selected = index === selectedIndex;
        // A failed line is not a warning dot — it is a line with no price in a
        // job whose total still looks believable. It reads red, and says so.
        const failure = result?.error ? lineErrorText(result.error) : null;
        // A material failure is not a failed line — labor still priced, and
        // the line still counts in the job total. It gets its own, milder
        // treatment: never "did not price", never $0.00.
        const materialFailed = !failure && Boolean(result?.material_error);
        const quoted =
          lineItem.material_price_override !== null &&
          lineItem.material_price_override !== undefined &&
          lineItem.material_price_override !== "";
        const rollup = childRollupSummary(result);

        return (
          <button
            type="button"
            key={index}
            className={`wb-item est-rail__row${selected ? " wb-item--on" : ""}${
              isCustom ? " est-rail__row--custom" : ""
            }${failure ? " est-rail__row--failed" : ""}${
              materialFailed ? " est-rail__row--matfail" : ""
            }`}
            aria-current={selected ? "true" : undefined}
            onClick={() => onSelect && onSelect(index)}
          >
            <span className="wb-item__t">
              <span>{name}</span>
              {/* A line a model proposed is visible from the rail alone — an
                  unreviewed draft must never blend into the job. */}
              {isAiDrafted(lineItem) ? (
                <span
                  className={`est-rail__badge est-rail__badge--ai${
                    isAiDraftPending(lineItem)
                      ? " est-rail__badge--pending"
                      : ""
                  }`}
                  title={
                    isAiDraftPending(lineItem)
                      ? "AI-drafted — not accepted yet"
                      : "AI-drafted, accepted by an estimator"
                  }
                >
                  AI
                </span>
              ) : null}
              {quoted ? (
                <span
                  className="est-rail__badge est-rail__badge--quoted"
                  title="This line's material price was quoted by hand, not priced by the rate card."
                >
                  Quoted
                </span>
              ) : null}
              {rollup?.count ? (
                <span className="est-rail__badge est-rail__badge--children">
                  +{rollup.count} component{rollup.count === 1 ? "" : "s"}
                </span>
              ) : null}
              <em className="est-rail__qty">×{Number(quantity)}</em>
            </span>
            {/* One line in a 250px rail, ellipsised. A failure sentence is
                exactly the text worth reading in full, so it also rides in a
                title — the dot below has had one all along. */}
            <span
              className={`wb-item__d${failure ? " est-rail__fail" : ""}`}
              role={failure ? "alert" : undefined}
              title={
                failure
                  ? `Did not price — ${failure}`
                  : materialFailed
                    ? MATERIAL_FAILURE_RAIL_NOTE
                    : undefined
              }
            >
              {failure
                ? `Did not price — ${failure}`
                : materialFailed
                  ? MATERIAL_FAILURE_RAIL_NOTE
                  : [typeInDetail, dimensions, material]
                      .filter(Boolean)
                      .join(" · ") ||
                    (isCustom
                      ? "Purchased items and build inputs"
                      : quotedLine
                        ? "Priced from a quoted price"
                        : "No dimensions yet")}
            </span>
            <span className="wb-item__m">
              {failure ? (
                <span className="est-rail__failtag">$0.00 — failed</span>
              ) : warnings.length || rollup?.hasErrors ? (
                <span
                  className="est-rail__dot"
                  title={[
                    ...warnings,
                    rollup?.hasErrors
                      ? "One or more components need attention."
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={`Needs attention: ${[...warnings, rollup?.hasErrors ? "One or more components need attention." : null].filter(Boolean).join(" ")}`}
                />
              ) : null}
              {failure ? null : amount ? (
                <span
                  title={
                    amount.kind === "cost"
                      ? "Line cost — open the cost trace for the customer price"
                      : "Customer price for this line"
                  }
                >
                  {formatCurrency(amount.value)}
                </span>
              ) : (
                <span className="est-rail__none" aria-label="No figure yet">
                  —
                </span>
              )}
            </span>
          </button>
        );
      })}

      {extrasIndex !== null ? (
        <button
          type="button"
          className={`wb-item est-rail__row${
            extrasIndex === selectedIndex ? " wb-item--on" : ""
          }`}
          aria-current={extrasIndex === selectedIndex ? "true" : undefined}
          onClick={() => onSelect && onSelect(extrasIndex)}
        >
          <span className="wb-item__t">
            <span>{JOB_EXTRAS_LABEL}</span>
          </span>
          <span className="wb-item__d">
            Job-level costs and pricing factors
          </span>
          <span className="wb-item__m">{extrasSummary || "—"}</span>
        </button>
      ) : null}

      <div className="est-rail__foot">
        <button
          type="button"
          className="wb-plain est-rail__add"
          onClick={onAddLine}
        >
          + Add from product type…
        </button>
        {/* Absent, not disabled, when the server has no composer configured —
            an entry point that errors on click is worse than none. */}
        {composerAvailable && onDraftWithAi ? (
          <button
            type="button"
            className="wb-plain est-rail__add est-rail__add--ai"
            onClick={onDraftWithAi}
          >
            ✨ Draft with AI
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default JobContentsRail;
